import api from "./api";
import { convertToJson } from "../utils";

const getCompanySettingsQuery = `query{
  settings {
    template
    defaultTab
    currencyEnabled
    allowedParentDomains
    googleAnalyticsEnabled
    currencies{
      code
      text
      order
    }
    showAnnualClosePrices
    historicalDataLimitInYear
    numberOfYearOnChart
    timezone
    styleURI
    instruments{
      id
      default
      shareName
      marketName
      currencyCode
      tickerName
      marketAbbreviation
      color
      order
      decimalSeparator
      thousandsSeparator
      decimalDigits
    }
    peerEnabled
    peers {
      id
      shareName
      marketName
      currencyCode
      tickerName
      marketAbbreviation
      color
      order
      decimalSeparator
      thousandsSeparator
      decimalDigits
    }
    format {
      shortDate
      longDate
      decimalSeparator
      thousandsSeparator
      negativeNumberFormat
      decimalDigits
      positiveNumberFormat
      positiveChangeFormat
      calendarDate
    }
    useLatinNumber
    enableExcelDownload
    showSupplierInfo
    showSupplierInfoLink
    showDisclaimerInfo
    showCookiePolicyInfo
    showDataDelayInfo
    numberOfYearOnChart
    useMarchFiscalYearEnd
    tickerRefreshTime
    enabledColorBlindMode
  }
  customPhrases{
    value
    key
  }
}`;

export const getCompanySettings = () =>
  api.POST(window.appSettings.spLookupApiUrl, {
    query: getCompanySettingsQuery,
  });

/**
 *
 * @param {import("../..").IInstrument['instrumentId'][]} instrumentIds
 * @returns
 */
const getTickerQuery = (instrumentIds = []) => `
query {
  instruments(instrumentIds: ${JSON.stringify(instrumentIds)}){
    instrumentId,shareName,currencyCode,last,change,changePercentage,marketStatus,ticker,marketName,iSIN,marketID,currencyCode,marketAbbreviation,startingDate,currency,latestDate
  }
}
`;

/**
 * @typedef { import("../..").IInstrument } IInstrument
 * @param {import("../..").IInstrument['instrumentId'][]} instrumentIds
 * @returns { Promise<{data: {instruments: IInstrument[]}}> }
 */
export const getTickers = (instrumentIds = []) =>
  api.POST(window.appSettings.sDataApiUrl, {
    query: getTickerQuery(instrumentIds),
  });

const getCurrenciesQuery = currencies => `
  query {
    currency {
      currenciesByCodes(codes:${JSON.stringify(currencies)}){
        code
        name
      }
    }
  }
`;
/**
 *
 * @param {string[]} currencies
 * @returns {Promise<{
 *  data: {
 *    currency: {
 *      currenciesByCodes: {code: string, name: string}[]
 *    }
 * }
 * }>}
 */
export const getCurrenciesName = currencies =>
  api.POST(window.appSettings.sDataApiUrl, {
    query: getCurrenciesQuery(currencies),
  });

/**
 *
 * @param {import("../..").IInstrument['instrumentId']} instrumentId
 * @returns
 */
const getOrderDepthsAndLatestTradesQuery = instrumentId => `
 query{
    latestTrades (instrumentId: ${instrumentId}) {
      time
      last
      volume
      buyer
      seller
      tradeLastSellerBuyer
      brokerBuyerFullName
      brokerSellerFullName
    }
    orderDepths (instrumentId: ${instrumentId}) {
      rowUpdated
      marketDepth {
        buyPrice
        buyVolume
        sellPrice
        sellVolume
      }
    }
 }
`;

/**
 *
 * @param {import("../..").IInstrument['instrumentId'][]} instrumentIds
 * @returns
 */
export const getOrderDepthsAndLatestTrades = instrumentId => {
  return api.POST(window.appSettings.spLookupApiUrl, {
    query: getOrderDepthsAndLatestTradesQuery(instrumentId),
  });
};

/**
 *
 * @param {import("../..").IInstrument['instrumentId']} instrumentId
 * @returns
 */
const getListTradeBaseOnPeriodQuery = dataQuery => {
  return `
  query{
    sharePrices(search: ${convertToJson(dataQuery)}) {
      instrumentId
      changeOfPeriodPercentDate
      changeOfPeriodPercent
      highestPrice
      highestPriceDate
      lowestPrice
      lowestPriceDate
      highestVolumeDate
      highestVolume
      lowestVolumeDate
      lowestVolume
      highestDailyChangePercentDate
      highestDailyChangePercent
      lowestDailyChangePercentDate
      lowestDailyChangePercent
      currency

    }
  }
`;
};

/**
 *
 * @param { import("../..").IGetAnnualClosePricesQuery } dataQuery
 * @returns
 */
const getAnnualClosePricesQuery = dataQuery => {
  return `query{
    annualClosePrices (search: ${convertToJson(dataQuery)}){
      instrumentId
      isin
      marketId
      datas {
        date
        price
      }
    }
  }`;
};

const getHistoricalSharesQuery = dataQuery => {
  return `
  query{
    historicalShares(search: ${convertToJson(dataQuery)}) {
      instrumentId
      low
      close
      high
      open
      volume
      currency
      date
    }
  }
`;
};

/**
 *
 * @param {import("../..").IHistoricalPricesSearch} search
 * @returns
 */
export const getHistoricalPricesQuery = search => `
  query {
    instrumentHistorical(
      instrumentId: ${search.instrumentId}
      fromDate: "${search.fromDate}"
      toDate: "${search.toDate}"
      isIntraday: false
      toCurrency: "${search.currency || ""}"
    ) {
      date
      instrumentId
      open
      close
      high
      low
      volume
    }
  }
`;

export const getHistoricalShares = queryData => {
  return api.POST(window.appSettings.spLookupApiUrl, {
    query: getHistoricalSharesQuery(queryData),
  });
};

/**
 *
 * @param {import("../..").IInstrument['instrumentId'][]} instrumentIds
 * @returns
 */
export const getListTradeBaseOnPeriod = queryData => {
  return api.POST(window.appSettings.spLookupApiUrl, {
    query: getListTradeBaseOnPeriodQuery(queryData),
  });
};

/**
 *
 * @param {import("../..").IGetAnnualClosePricesQuery} queryData
 * @returns { Promise<import("../..").IGetAnnualClosePricesResponse> }
 */
export const getAnnualClosePrices = queryData => {
  return api.POST(window.appSettings.spLookupApiUrl, {
    query: getAnnualClosePricesQuery(queryData),
  });
};

/**
 *
 * @param {import("../..").IHistoricalPricesSearch} search
 * @returns { Promise<import("../..").IHistoricalPricesResponse> }
 */
export const getHistoricalPrices = search => {
  return api.POST(window.appSettings.sDataApiUrl, {
    query: getHistoricalPricesQuery(search),
  });
};
